import type {
  Extension,
  QuestionnaireItem as FhirQuestionnaireItem,
  Questionnaire,
  QuestionnaireItemEnableWhen,
} from "@medplum/fhirtypes";
import {
  asMemberId,
  type EnablementCondition,
  InvalidCatalogDefinitionError,
  isQuestionnaireScoreCalculation,
  type QuestionnaireAnswerValue,
  type QuestionnaireCondition,
  type QuestionnaireConditionValue,
  type QuestionnaireItem,
  type QuestionnaireMemberMarks,
  type QuestionnaireQuestion,
  type QuestionnaireScoreBand,
  type QuestionnaireSection,
  type StoredQuestionnaireDefinitionProps,
  type TextFormat,
} from "@repo/domain";
import {
  extensionUrl,
  MAX_VALUE_URL,
  MIN_VALUE_URL,
  ORDINAL_VALUE_EXTENSION_URL,
  QUESTIONNAIRE_ITEM_CONTROL_SYSTEM,
  QUESTIONNAIRE_ITEM_CONTROL_URL,
  SLIDER_STEP_VALUE_URL as STEP_URL,
  QUESTIONNAIRE_UNIT_URL as UNIT_URL,
} from "@repo/fhir-systems";
import { catalogNameToFhir } from "../../lib/catalog-name";
import { UCUM_SYSTEM, unitFromCoding } from "../../lib/ucum";
import { omitUndefined } from "../omit-undefined";
import { type Codec, fp } from "./codec";
import { factConditionExtensions, withFactConditions } from "./patient-fact-condition";
import { malformed, required } from "./read";

// ============================================================
// QuestionnaireDefinition ⇄ Questionnaire
//
// THIS CODEC OWNS EVERY FHIR WORD. The model it carries knows nothing of
// `linkId`, `enableWhen`, `answerOption` or the sixteen FHIR item types — the
// entity it replaces WAS the resource, and the seam is the point.
//
// `Questionnaire` STORES THREE MODELS: QuestionnaireDefinition, ExamDefinition
// and FindingDefinition. Something has to say which one a row is, and that
// discriminator is never a domain field — nothing in the domain branches on it,
// and an entity carrying a `type` would be storage leaking upward. The profile
// is stamped here on write; refusing a row that does not carry it is the
// mapper's guard, because deciding what a row IS is not translation.
//
// NO RESOURCE ID CROSSES THE BOUNDARY. The lineage identity is the canonical
// `url` and nothing references a questionnaire by database id, so the model
// carries none and this codec never writes one: the session's merge-over-prior
// keeps the stored id on an update, and a create needs no back-fill.
// ============================================================

export const QUESTIONNAIRE_DEFINITION_PROFILE = extensionUrl("questionnaire-definition");

/**
 * The member-id high-water marks. PERSISTED because they are aggregate state:
 * derived from the members present, a removed member's identity would be
 * reissued to a different question, and every recorded answer citing the old
 * one would silently repoint.
 */
const SCORES_URL = extensionUrl("questionnaire-scores");
const TOTAL_BANDS_URL = extensionUrl("questionnaire-total-bands");
export const MEMBER_MARKS_URL = extensionUrl("questionnaire-member-marks");
const MIN_OCCURRENCES_URL = extensionUrl("questionnaire-min-occurrences");
const TEXT_FORMAT_URL = extensionUrl("questionnaire-text-format");
const OPTION_DETAIL_URL = extensionUrl("questionnaire-option-detail");
const ASK_YES_NO_FIRST_URL = extensionUrl("questionnaire-ask-yes-no-first");

type Props = StoredQuestionnaireDefinitionProps;

/** An optional key, present only when there is something to say. Absent means "the entity has no opinion". */
function optional<T>(key: string, value: T | undefined): Record<string, T> {
  return value === undefined ? {} : ({ [key]: value } as Record<string, T>);
}

// ============================================================
// Sections and items
//
// The domain's two levels ride on FHIR's arbitrary `item` recursion: a
// top-level group is a section, a nested group is a repeating group. Depth is
// the discriminator, so no marker extension is needed and a hand-written
// resource cannot claim to be a section three levels down.
// ============================================================

/**
 * FHIR needs this whenever an item carries more than one `enableWhen`, and the
 * domain models only the departure from the default: `conditionBehavior` is
 * present exactly when it is "any". "all" here is FHIR's own spelling of what
 * an absent domain value already means, not a fact this codec invents — the
 * same translation as `availability` ⇄ `status`.
 */
function enableBehaviorToFhir(
  conditions: readonly EnablementCondition[] | undefined,
  behavior: "any" | "all" | undefined,
): "any" | "all" | undefined {
  if (!conditions?.length) return undefined;
  return behavior === "any" ? "any" : "all";
}

// A section asked as one screen is SDC's `page` item control, the one control
// this model reads; any other is a renderer hint it has no use for.
const PAGE_CONTROL = {
  url: QUESTIONNAIRE_ITEM_CONTROL_URL,
  valueCodeableConcept: { coding: [{ system: QUESTIONNAIRE_ITEM_CONTROL_SYSTEM, code: "page" }] },
};

function isPage(item: FhirQuestionnaireItem): boolean {
  return (item.extension ?? []).some(
    (e) =>
      e.url === QUESTIONNAIRE_ITEM_CONTROL_URL &&
      (e.valueCodeableConcept?.coding ?? []).some(
        (c) => c.system === QUESTIONNAIRE_ITEM_CONTROL_SYSTEM && c.code === "page",
      ),
  );
}

export function sectionToFhir(section: QuestionnaireSection): FhirQuestionnaireItem {
  const extensions = [...(section.showTogether ? [PAGE_CONTROL] : []), ...factConditionExtensions(section.conditions)];
  return omitUndefined({
    linkId: section.id as string,
    type: "group" as const,
    text: section.label,
    extension: extensions.length ? extensions : undefined,
    enableWhen: conditionsToFhir(section.conditions),
    enableBehavior: enableBehaviorToFhir(section.conditions, section.conditionBehavior),
    item: section.items.map(itemToFhir),
  });
}

export function sectionFromFhir(row: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireSection {
  if (item.type !== "group") {
    throw new InvalidCatalogDefinitionError(`Top-level item '${item.linkId}' is not a section`, { url: row.url });
  }
  return omitUndefined({
    id: asMemberId(item.linkId),
    label: required(row, "section.label", item.text),
    conditions: conditionsFromFhir(item.enableWhen, item.extension),
    conditionBehavior: item.enableBehavior === "any" ? ("any" as const) : undefined,
    showTogether: isPage(item) || undefined,
    items: (item.item ?? []).map((child) => itemFromFhir(row, child)),
  });
}

function itemToFhir(item: QuestionnaireItem): FhirQuestionnaireItem {
  if (item.kind === "Display") {
    return { linkId: item.id as string, type: "display", text: item.text };
  }
  if (item.kind === "RepeatingGroup") {
    return omitUndefined({
      linkId: item.id as string,
      type: "group" as const,
      text: item.label,
      // FHIR's own spelling for "this group may be filled in more than once".
      repeats: true,
      extension: [{ url: MIN_OCCURRENCES_URL, valueInteger: item.minOccurrences }],
      item: item.items.map(itemToFhir),
    });
  }
  return questionToFhir(item);
}

function itemFromFhir(row: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireItem {
  if (item.type === "display") {
    return { kind: "Display", id: asMemberId(item.linkId), text: required(row, "display.text", item.text) };
  }
  if (item.type === "group") {
    return {
      kind: "RepeatingGroup",
      id: asMemberId(item.linkId),
      label: required(row, "repeating group.label", item.text),
      // Always written, so a row without it is malformed rather than a group
      // that may be left empty.
      minOccurrences: required(
        row,
        "repeating group.minOccurrences",
        item.extension?.find((e) => e.url === MIN_OCCURRENCES_URL)?.valueInteger,
      ),
      items: (item.item ?? []).map((child) => itemFromFhir(row, child)) as (QuestionnaireQuestion | never)[],
    };
  }
  return questionFromFhir(row, item);
}

function questionToFhir(question: QuestionnaireQuestion): FhirQuestionnaireItem {
  const value = question.value;
  return omitUndefined({
    linkId: question.id as string,
    // `open-choice` is a FHIR spelling, not a domain type: the model says
    // `allowFreeText` on the Choice value object, and this is where that
    // becomes storage.
    type: fhirItemType(value),
    text: question.label,
    item:
      question.help === undefined
        ? undefined
        : [
            {
              linkId: `${question.id}-help`,
              type: "display" as const,
              text: question.help,
              extension: [
                {
                  url: QUESTIONNAIRE_ITEM_CONTROL_URL,
                  valueCodeableConcept: { coding: [{ system: QUESTIONNAIRE_ITEM_CONTROL_SYSTEM, code: "help" }] },
                },
              ],
            },
          ],
    required: question.required || undefined,
    repeats: value.kind === "Choice" && value.cardinality === "Multiple" ? true : undefined,
    maxLength: value.kind === "Text" ? value.maxLength : undefined,
    answerOption:
      value.kind === "Choice"
        ? value.options.map((option) =>
            omitUndefined({
              valueCoding: { code: option.id as string, display: option.label },
              extension:
                option.points === undefined && option.detail === undefined
                  ? undefined
                  : [
                      ...(option.points === undefined
                        ? []
                        : [{ url: ORDINAL_VALUE_EXTENSION_URL, valueDecimal: option.points }]),
                      ...(option.detail === undefined ? [] : [{ url: OPTION_DETAIL_URL, valueString: option.detail }]),
                    ],
            }),
          )
        : undefined,
    enableWhen: conditionsToFhir(question.conditions),
    enableBehavior: enableBehaviorToFhir(question.conditions, question.conditionBehavior),
    extension: questionExtensionsToFhir(question),
  });
}

function questionExtensionsToFhir(question: QuestionnaireQuestion): Extension[] | undefined {
  const extensions = [
    ...factConditionExtensions(question.conditions),
    ...(valueConstraintsToFhir(question.value) ?? []),
    ...(question.askYesNoFirst ? [{ url: ASK_YES_NO_FIRST_URL, valueBoolean: true }] : []),
  ];
  return extensions.length ? extensions : undefined;
}

function questionFromFhir(row: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireQuestion {
  return omitUndefined({
    kind: "Question" as const,
    id: asMemberId(item.linkId),
    label: required(row, "question.label", item.text),
    help: item.item?.find(
      (child) =>
        child.type === "display" &&
        child.extension?.some(
          (e) =>
            e.url === QUESTIONNAIRE_ITEM_CONTROL_URL &&
            e.valueCodeableConcept?.coding?.some(
              (c) => c.system === QUESTIONNAIRE_ITEM_CONTROL_SYSTEM && c.code === "help",
            ),
        ),
    )?.text,
    required: item.required === true,
    value: valueFromFhir(row, item),
    conditions: conditionsFromFhir(item.enableWhen, item.extension),
    conditionBehavior: item.enableBehavior === "any" ? ("any" as const) : undefined,
    askYesNoFirst: item.extension?.some((e) => e.url === ASK_YES_NO_FIRST_URL && e.valueBoolean) || undefined,
  });
}

function fhirItemType(value: QuestionnaireAnswerValue): NonNullable<FhirQuestionnaireItem["type"]> {
  switch (value.kind) {
    case "Quantity":
      return "quantity";
    case "Integer":
      return "integer";
    case "Choice":
      return value.allowFreeText ? "open-choice" : "choice";
    case "Boolean":
      return "boolean";
    case "Text":
      // FHIR's own split: `text` is a paragraph, `string` one line. Email and
      // Phone are single lines; which one rides on TEXT_FORMAT_URL.
      return value.format === undefined ? "text" : "string";
    case "Date":
      return "date";
    case "Attachment":
      return "attachment";
    default:
      return "url";
  }
}

function valueConstraintsToFhir(value: QuestionnaireAnswerValue): Extension[] | undefined {
  const extensions: Extension[] = [];
  if (value.kind === "Quantity") {
    if (value.unit !== undefined) {
      extensions.push({
        url: UNIT_URL,
        valueCoding: { system: UCUM_SYSTEM, code: value.unit.code, display: value.unit.display },
      });
    }
    if (value.min !== undefined) extensions.push({ url: MIN_VALUE_URL, valueDecimal: value.min });
    if (value.max !== undefined) extensions.push({ url: MAX_VALUE_URL, valueDecimal: value.max });
    if (value.step !== undefined) extensions.push({ url: STEP_URL, valueDecimal: value.step });
  }
  if (value.kind === "Integer") {
    if (value.min !== undefined) extensions.push({ url: MIN_VALUE_URL, valueInteger: value.min });
    if (value.max !== undefined) extensions.push({ url: MAX_VALUE_URL, valueInteger: value.max });
    if (value.step !== undefined) extensions.push({ url: STEP_URL, valueInteger: value.step });
  }
  if (value.kind === "Text" && (value.format === "Email" || value.format === "Phone")) {
    extensions.push({ url: TEXT_FORMAT_URL, valueCode: value.format === "Email" ? "email" : "phone" });
  }
  return extensions.length > 0 ? extensions : undefined;
}

function valueFromFhir(row: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireAnswerValue {
  const bound = (url: string): number | undefined => {
    const extension = item.extension?.find((candidate) => candidate.url === url);
    return extension?.valueDecimal ?? extension?.valueInteger;
  };
  switch (item.type) {
    case "quantity":
      return omitUndefined({
        kind: "Quantity" as const,
        unit: unitFromCoding(item.extension?.find((e) => e.url === UNIT_URL)?.valueCoding),
        min: bound(MIN_VALUE_URL),
        max: bound(MAX_VALUE_URL),
        step: bound(STEP_URL),
      });
    case "integer":
      return omitUndefined({
        kind: "Integer" as const,
        min: bound(MIN_VALUE_URL),
        max: bound(MAX_VALUE_URL),
        step: bound(STEP_URL),
      });
    case "choice":
    case "open-choice":
      return {
        kind: "Choice",
        cardinality: item.repeats ? "Multiple" : "Single",
        allowFreeText: item.type === "open-choice",
        options: (item.answerOption ?? []).map((option) =>
          omitUndefined({
            id: option.valueCoding?.code ? asMemberId(option.valueCoding.code) : undefined,
            label: required(row, "choice option.label", option.valueCoding?.display),
            detail: option.extension?.find((e) => e.url === OPTION_DETAIL_URL)?.valueString,
            points: option.extension?.find((e) => e.url === ORDINAL_VALUE_EXTENSION_URL)?.valueDecimal,
          }),
        ),
      };
    case "boolean":
      return { kind: "Boolean" };
    case "date":
      return { kind: "Date" };
    case "attachment":
      return { kind: "Attachment" };
    case "url":
      return { kind: "Url" };
    case "text":
      return omitUndefined({ kind: "Text" as const, maxLength: item.maxLength });
    case "string":
      return omitUndefined({ kind: "Text" as const, maxLength: item.maxLength, format: textFormat(item) });
    default:
      throw new InvalidCatalogDefinitionError(
        `Questionnaire item '${item.linkId}' has unsupported type '${item.type}'`,
      );
  }
}

function textFormat(item: FhirQuestionnaireItem): TextFormat {
  const code = item.extension?.find((e) => e.url === TEXT_FORMAT_URL)?.valueCode;
  return code === "email" ? "Email" : code === "phone" ? "Phone" : "SingleLine";
}

// ============================================================
// Conditions
// ============================================================

function conditionsToFhir(
  conditions: readonly EnablementCondition[] | undefined,
): QuestionnaireItemEnableWhen[] | undefined {
  if (!conditions?.length) return undefined;
  const questions = conditions.filter((condition): condition is QuestionnaireCondition => !("fact" in condition));
  if (!questions.length) return undefined;
  return questions.map((condition) => ({
    question: condition.question as string,
    operator: condition.operator,
    ...answerToFhir(condition.value),
  }));
}

function answerToFhir(value: QuestionnaireConditionValue): Partial<QuestionnaireItemEnableWhen> {
  switch (value.kind) {
    case "Boolean":
      return { answerBoolean: value.value };
    case "Integer":
      return { answerInteger: value.value };
    case "Quantity":
      return { answerDecimal: value.value };
    case "Text":
      return { answerString: value.value };
    case "Date":
      return { answerDate: value.value };
    default:
      // A choice condition names the OPTION IDENTITY, never its label: a label
      // is content an author may correct, and a condition keyed on one would
      // change meaning when they did.
      return { answerCoding: { code: value.optionId as string } };
  }
}

function conditionsFromFhir(
  enableWhen: QuestionnaireItemEnableWhen[] | undefined,
  extensions: Extension[] | undefined,
): readonly EnablementCondition[] | undefined {
  const conditions = withFactConditions(
    (enableWhen ?? []).map(
      (condition): QuestionnaireCondition => ({
        question: asMemberId(condition.question),
        operator: condition.operator,
        value: answerFromFhir(condition),
      }),
    ),
    extensions,
  );
  return conditions.length ? conditions : undefined;
}

function answerFromFhir(condition: QuestionnaireItemEnableWhen): QuestionnaireConditionValue {
  if (condition.answerBoolean !== undefined) return { kind: "Boolean", value: condition.answerBoolean };
  if (condition.answerInteger !== undefined) return { kind: "Integer", value: condition.answerInteger };
  if (condition.answerDecimal !== undefined) return { kind: "Quantity", value: condition.answerDecimal };
  if (condition.answerString !== undefined) return { kind: "Text", value: condition.answerString };
  if (condition.answerDate !== undefined) return { kind: "Date", value: condition.answerDate };
  if (condition.answerCoding?.code) return { kind: "Choice", optionId: asMemberId(condition.answerCoding.code) };
  throw new InvalidCatalogDefinitionError(`Condition on '${condition.question}' compares against nothing`);
}

// ============================================================
// Member marks
// ============================================================

function extensionsToFhir(
  marks: Partial<QuestionnaireMemberMarks> | undefined,
  prior: Questionnaire | null,
  scores: Props["scores"],
  totalBands: Props["totalBands"],
): Extension[] {
  // Rebuilt over the stored array, not replacing it: `extension` is one FHIR
  // field, so writing only ours would drop whatever another writer put there.
  return [
    ...(prior?.extension ?? []).filter(
      (candidate) =>
        candidate.url !== MEMBER_MARKS_URL && candidate.url !== SCORES_URL && candidate.url !== TOTAL_BANDS_URL,
    ),
    { url: SCORES_URL, valueString: JSON.stringify(scores ?? []) },
    ...(totalBands?.length ? [{ url: TOTAL_BANDS_URL, valueString: JSON.stringify(totalBands) }] : []),
    ...(marks === undefined ? [] : [{ url: MEMBER_MARKS_URL, valueString: JSON.stringify(marks) }]),
  ];
}

/**
 * One JSON string, and a row may carry none of it. ABSENT AND UNREADABLE ARE
 * NOT THE SAME THING, and reading them the same way is what this stopped
 * doing. Absent is restoration's business: a lineage that never had marks
 * raises its sequences to clear the members present, which is exactly right
 * for one that has never removed a member. Unreadable is a row that DID carry
 * marks, and the one thing marks remember that nothing else can is the members
 * that were REMOVED — so falling back to the present members reissues a
 * removed member's id to a different question, under answers that still cite
 * it. That is silent, it is clinical, and no later read can detect it.
 *
 * Shape is checked here because there is none to check against: `JSON.parse`
 * hands back `any`, the counts feed `Math.max`, and a mark stored as a string
 * makes every sequence `NaN` without anything failing.
 */
export function marksFromFhir(resource: Questionnaire): Partial<QuestionnaireMemberMarks> | undefined {
  const encoded = resource.extension?.find((candidate) => candidate.url === MEMBER_MARKS_URL)?.valueString;
  if (!encoded) return undefined;
  const parsed = parseStored(resource, "marks", encoded);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    malformed(resource, "marks", "an object", parsed);
  }
  const stored = parsed as Record<string, unknown>;
  return {
    ...markAt(resource, stored, "section"),
    ...markAt(resource, stored, "option"),
    ...markAt(resource, stored, "item"),
    ...markAt(resource, stored, "score"),
  };
}

function parseStored(resource: Questionnaire, field: string, encoded: string): unknown {
  try {
    return JSON.parse(encoded) as unknown;
  } catch {
    return malformed(resource, field, "JSON", encoded);
  }
}

/** One sequence's mark. Absent is partial marks, which storage genuinely has; anything else is a bad row. */
function markAt(
  resource: Questionnaire,
  stored: Record<string, unknown>,
  key: keyof QuestionnaireMemberMarks,
): Partial<QuestionnaireMemberMarks> {
  const value = stored[key];
  if (value === undefined) return {};
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    malformed(resource, `marks.${key}`, "a non-negative whole number", value);
  }
  return { [key]: value };
}

// ---------- the resource ----------

function scoresFromFhir(resource: Questionnaire): NonNullable<Props["scores"]> {
  const encoded = resource.extension?.find((e) => e.url === SCORES_URL)?.valueString;
  if (encoded === undefined) return [];
  const parsed = parseStored(resource, "scores", encoded);
  if (!Array.isArray(parsed)) return malformed(resource, "scores", "an array", parsed);
  return parsed.map((score) => {
    if (
      !score ||
      typeof score.id !== "string" ||
      typeof score.label !== "string" ||
      !Array.isArray(score.itemIds) ||
      score.itemIds.some((id: unknown) => typeof id !== "string") ||
      (score.calculation !== undefined && !isQuestionnaireScoreCalculation(score.calculation))
    ) {
      return malformed(resource, "scores", "score members", score);
    }
    const bands = score.bands === undefined ? undefined : bandsFromStored(resource, "scores", score.bands);
    return {
      id: asMemberId(score.id),
      label: score.label,
      itemIds: score.itemIds.map(asMemberId),
      ...optional("bands", bands),
      ...optional("calculation", score.calculation),
    };
  });
}

function totalBandsFromFhir(resource: Questionnaire): Props["totalBands"] {
  const encoded = resource.extension?.find((e) => e.url === TOTAL_BANDS_URL)?.valueString;
  if (encoded === undefined) return undefined;
  return bandsFromStored(resource, "totalBands", parseStored(resource, "totalBands", encoded));
}

function bandsFromStored(resource: Questionnaire, field: string, stored: unknown): QuestionnaireScoreBand[] {
  if (!Array.isArray(stored)) return malformed(resource, field, "an array of bands", stored);
  return stored.map((band) => {
    if (
      !band ||
      typeof band.label !== "string" ||
      typeof band.from !== "number" ||
      (band.to !== undefined && typeof band.to !== "number")
    ) {
      return malformed(resource, field, "a band with a label and a numeric start", band);
    }
    return { label: band.label, from: band.from, ...optional("to", band.to) };
  });
}

export function decodeQuestionSet(resource: Questionnaire): Props {
  return {
    url: required(resource, "url", resource.url),
    // Compared and stored as an integer. A string version sorts "9" above
    // "10", which silently picks the wrong current revision from ten on.
    version: Number(required(resource, "version", resource.version)),
    // `date` is the MINT date, stamped once. As-of reconstruction resolves
    // against it rather than `meta.lastUpdated`, which a correction moves.
    mintedAt: required(resource, "mintedAt", resource.date),
    name: required(resource, "name", resource.title),
    ...optional("description", resource.description),
    availability: resource.status === "active" ? "Active" : "Inactive",
    scores: scoresFromFhir(resource),
    ...optional("totalBands", totalBandsFromFhir(resource)),
    sections: (resource.item ?? []).map((item) => sectionFromFhir(resource, item)),
    ...optional("marks", marksFromFhir(resource)),
  };
}

export function encodeQuestionSet(props: Props, prior: Questionnaire | null): Questionnaire {
  return omitUndefined({
    resourceType: "Questionnaire" as const,
    status: props.availability === "Active" ? ("active" as const) : ("retired" as const),
    url: props.url,
    version: String(props.version),
    // The mint date, written explicitly and never recomputed. As-of
    // reconstruction resolves against this: `meta.lastUpdated` moves on a
    // correction and would make an old revision look minted on the day
    // someone fixed a typo.
    date: props.mintedAt,
    ...catalogNameToFhir(props.name),
    description: props.description,
    item: props.sections.map(sectionToFhir),
    extension: extensionsToFhir(props.marks, prior, props.scores, props.totalBands),
  });
}

export const questionSetContentCodec: Codec<Props, keyof Props, Questionnaire> = {
  resourceType: "Questionnaire",
  reads: {
    url: [fp("url")],
    version: [fp("version")],
    mintedAt: [fp("date")],
    name: [fp("title")],
    description: [fp("description")],
    availability: [fp("status")],
    sections: [fp("item")],
    scores: [fp(`extension.where(url = '${SCORES_URL}').valueString`)],
    totalBands: [fp(`extension.where(url = '${TOTAL_BANDS_URL}').valueString`)],
    marks: [fp(`extension.where(url = '${MEMBER_MARKS_URL}').valueString`)],
  },
  // `item` and `description` are rebuilt from complete entity state, so their
  // EMPTINESS is meaningful: without this, removing the last section would
  // merge to no change at all and the commit would report success having
  // written nothing.
  owns: [fp("item"), fp("description")],
  decode: decodeQuestionSet,
  encode: encodeQuestionSet,
};
